import React, { useState } from 'react';
import { Box, Typography, Paper, Card, CardContent, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { Undo as UndoIcon } from '@mui/icons-material';
import { format } from 'date-fns';

/**
 * History Tab - Archive of answered questions
 */
const HistoryTab = ({ questions, onUndoAnswer }) => {
  const [confirmDialog, setConfirmDialog] = useState({ open: false, questionId: null, questionText: '' });

  const answeredQuestions = questions
    .filter(q => q.status === 'answered')
    .sort((a, b) => {
      const dateA = a.publishDate?.toDate ? a.publishDate.toDate() : new Date(a.publishDate);
      const dateB = b.publishDate?.toDate ? b.publishDate.toDate() : new Date(b.publishDate);
      return dateA - dateB; // Oldest first
    });

  const handleUndoClick = (questionId, questionText) => {
    setConfirmDialog({ open: true, questionId, questionText });
  };

  const handleConfirmUndo = () => {
    onUndoAnswer(confirmDialog.questionId);
    setConfirmDialog({ open: false, questionId: null, questionText: '' });
  };

  const handleCancelUndo = () => {
    setConfirmDialog({ open: false, questionId: null, questionText: '' });
  };

  return (
    <Box>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: 'success.light' }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          ✓ History ({answeredQuestions.length})
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Questions that have been answered
        </Typography>
      </Paper>

      {/* History List */}
      {answeredQuestions.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No answered questions yet
          </Typography>
        </Paper>
      ) : (
        answeredQuestions.map((question) => {
          const sentDate = question.publishDate?.toDate 
            ? question.publishDate.toDate() 
            : new Date(question.publishDate);
          const answeredDate = question.answerDate?.toDate 
            ? question.answerDate.toDate() 
            : null;

          return (
            <Card key={question.id} sx={{ mb: 2, borderRadius: 3, opacity: 0.8 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="h6" gutterBottom>
                      {question.questionText}
                    </Typography>

                    {/* Answer Preview */}
                    {question.momAnswer && (
                      <Box 
                        sx={{ 
                          mb: 2, 
                          p: 2, 
                          bgcolor: 'grey.50', 
                          borderRadius: 2,
                          borderLeft: '3px solid',
                          borderColor: 'success.main'
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            fontFamily: 'Georgia, serif',
                            fontStyle: 'italic',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.6,
                          }}
                        >
                          {question.momAnswer}
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">
                        📤 Sent: {format(sentDate, 'MMM d, yyyy')}
                      </Typography>
                      {answeredDate && (
                        <Typography variant="caption" color="text.secondary">
                          ✓ Answered: {format(answeredDate, 'MMM d, yyyy')}
                        </Typography>
                      )}
                      <Chip label="Answered" color="success" size="small" />
                    </Box>
                  </Box>

                  {/* Undo Button */}
                  <Tooltip title="Undo answer (reset to 'sent' status)">
                    <IconButton
                      color="warning"
                      onClick={() => handleUndoClick(question.id, question.questionText)}
                      sx={{ ml: 1 }}
                    >
                      <UndoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelUndo}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Undo Answer?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to undo the answer for this question? This will reset it back to "sent" status.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
            <Typography variant="body2" fontStyle="italic">
              "{confirmDialog.questionText}"
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUndo} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmUndo} color="warning" variant="contained" autoFocus>
            Undo Answer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoryTab;
